"use client";

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from "react";
import React, { createContext, useState, useContext, useCallback, useMemo } from "react";
import { MyStreamWaveTab } from "@/types/waves.types";
export enum WaveVotingState {
  NOT_STARTED = "NOT_STARTED",
  ONGOING = "ONGOING",
  ENDED = "ENDED",
}

// Define a type for the updateAvailableTabs parameters
type WaveTabParams = {
  isChatWave: boolean;
  isMemesWave: boolean;
  votingState: WaveVotingState;
  hasFirstDecisionPassed: boolean;
};
interface ContentTabContextType {
  activeContentTab: MyStreamWaveTab;
  setActiveContentTab: (tab: MyStreamWaveTab) => void;
  availableTabs: MyStreamWaveTab[];
  updateAvailableTabs: (params: WaveTabParams | null) => void;
}

// Create the context with a default value
const ContentTabContext = createContext<ContentTabContextType>({
  activeContentTab: MyStreamWaveTab.CHAT,
  setActiveContentTab: () => {},
  availableTabs: [MyStreamWaveTab.CHAT],
  updateAvailableTabs: () => {}
});

// Export a provider component
export const ContentTabProvider: React.FC<{
  children: ReactNode;
}> = t0 => {
  const $ = _c(13);
  const {
    children
  } = t0;
  const [activeContentTabRaw, setActiveContentTabRaw] = useState(MyStreamWaveTab.CHAT);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [MyStreamWaveTab.CHAT];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [availableTabs, setAvailableTabs] = useState(t1);
  let t2;
  if ($[1] !== activeContentTabRaw) {
    t2 = params => {
      if (!params) {
        setAvailableTabs([MyStreamWaveTab.CHAT]);
        if (activeContentTabRaw !== MyStreamWaveTab.CHAT) {
          setActiveContentTabRaw(MyStreamWaveTab.CHAT);
        }
        return;
      }
      const {
        isChatWave,
        isMemesWave,
        votingState,
        hasFirstDecisionPassed
      } = params;
      if (isChatWave) {
        setAvailableTabs([MyStreamWaveTab.CHAT]);
        if (activeContentTabRaw !== MyStreamWaveTab.CHAT) {
          setActiveContentTabRaw(MyStreamWaveTab.CHAT);
        }
        return;
      }
      if (isMemesWave) {
        const tabs = [MyStreamWaveTab.CHAT];
        if (votingState !== WaveVotingState.ENDED) {
          tabs.push(MyStreamWaveTab.LEADERBOARD);
        }
        if (hasFirstDecisionPassed) {
          tabs.push(MyStreamWaveTab.WINNERS);
        }
        tabs.push(MyStreamWaveTab.MY_VOTES);
        tabs.push(MyStreamWaveTab.OUTCOME);
        tabs.push(MyStreamWaveTab.FAQ);
        setAvailableTabs(tabs);
        if (!tabs.includes(activeContentTabRaw)) {
          setActiveContentTabRaw(MyStreamWaveTab.CHAT);
        }
        return;
      }
      const tabs_0 = [MyStreamWaveTab.CHAT];
      if (votingState !== WaveVotingState.ENDED) {
        tabs_0.push(MyStreamWaveTab.LEADERBOARD);
      }
      if (hasFirstDecisionPassed) {
        tabs_0.push(MyStreamWaveTab.WINNERS);
      }
      tabs_0.push(MyStreamWaveTab.OUTCOME);
      tabs_0.push(MyStreamWaveTab.MY_VOTES);
      setAvailableTabs(tabs_0);
      if (!tabs_0.includes(activeContentTabRaw)) {
        if (tabs_0.includes(MyStreamWaveTab.LEADERBOARD)) {
          setActiveContentTabRaw(MyStreamWaveTab.LEADERBOARD);
        } else {
          setActiveContentTabRaw(MyStreamWaveTab.CHAT);
        }
      }
    };
    $[1] = activeContentTabRaw;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const updateAvailableTabs = t2;
  let t3;
  if ($[3] !== availableTabs) {
    t3 = tab => {
      if (availableTabs.includes(tab)) {
        setActiveContentTabRaw(tab);
      } else {
        setActiveContentTabRaw(MyStreamWaveTab.CHAT);
      }
    };
    $[3] = availableTabs;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const setActiveContentTab = t3;
  let t4;
  if ($[5] !== activeContentTabRaw || $[6] !== availableTabs || $[7] !== setActiveContentTab || $[8] !== updateAvailableTabs) {
    t4 = {
      activeContentTab: activeContentTabRaw,
      setActiveContentTab,
      availableTabs,
      updateAvailableTabs
    };
    $[5] = activeContentTabRaw;
    $[6] = availableTabs;
    $[7] = setActiveContentTab;
    $[8] = updateAvailableTabs;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const contextValue = t4;
  let t5;
  if ($[10] !== children || $[11] !== contextValue) {
    t5 = <ContentTabContext.Provider value={contextValue}>{children}</ContentTabContext.Provider>;
    $[10] = children;
    $[11] = contextValue;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
};

// Export a hook for using the context
export const useContentTab = () => {
  return useContext(ContentTabContext);
};